import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isAndroidApp = window.Android !== undefined;
    this.appState = 'foreground';
    this.backgroundTimer = null;
    this.BACKGROUND_DISCONNECT_DELAY = 30000; // 30초
    this.currentProjectId = null; // 현재 참가한 프로젝트 ID
  }

  connect(token) {
    if (this.socket?.connected) {
      console.log('[SocketService] Already connected');
      return this.socket;
    }

    // URL 설정 - 프로덕션에서는 상대 경로 사용
    const socketUrl = process.env.NODE_ENV === 'production' ? '/' : (process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000');
    
    console.log('[SocketService] Connecting to:', socketUrl);
    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 45000,
      autoConnect: true,
      forceNew: false
    });

    // 전역 socket 객체로 등록
    window.socket = this.socket;

    this.socket.on('connect', () => {
      console.log('Socket connected');
      
      // 재연결 시 현재 프로젝트에 다시 참가
      if (this.currentProjectId) {
        this.joinProject(this.currentProjectId);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      window.socket = null;
    }
    
    // 타이머가 있다면 정리
    if (this.backgroundTimer) {
      clearTimeout(this.backgroundTimer);
      this.backgroundTimer = null;
    }
  }

  joinProject(projectId) {
    this.currentProjectId = projectId; // 현재 프로젝트 ID 저장
    if (this.socket?.connected) {
      this.socket.emit('join-project', projectId);
    }
  }

  leaveProject(projectId) {
    if (this.socket?.connected) {
      this.socket.emit('leave-project', projectId);
    }
    if (this.currentProjectId === projectId) {
      this.currentProjectId = null;
    }
  }

  // Android 앱 상태 업데이트
  updateAppState(state) {
    this.appState = state;
    console.log(`App state changed to: ${state}`);
    
    // 서버에 앱 상태 즉시 알림
    if (this.socket?.connected) {
      this.socket.emit('app-state-change', state);
    }
    
    if (state === 'background') {
      // 백그라운드로 전환 시 30초 후 연결 해제 타이머 시작
      console.log('Starting background disconnect timer (30s)');
      this.backgroundTimer = setTimeout(() => {
        if (this.appState === 'background' && this.socket?.connected) {
          console.log('Disconnecting socket after 30s in background');
          this.disconnect();
        }
      }, this.BACKGROUND_DISCONNECT_DELAY);
    } else if (state === 'foreground') {
      // 포그라운드로 복귀 시
      if (this.backgroundTimer) {
        // 타이머 취소
        clearTimeout(this.backgroundTimer);
        this.backgroundTimer = null;
        console.log('Cancelled background disconnect timer');
      }
      
      // Socket이 끊어져 있다면 재연결
      if (!this.socket?.connected) {
        console.log('Reconnecting socket on foreground');
        const token = localStorage.getItem('token');
        if (token) {
          this.connect(token);
        }
      }
    }
  }

  isAppInBackground() {
    // Android 앱인 경우
    if (this.isAndroidApp && window.Android?.isAppInForeground) {
      return !window.Android.isAppInForeground();
    }
    
    // 웹 브라우저인 경우 (페이지가 숨겨진 상태 확인)
    return document.hidden || document.visibilityState === 'hidden';
  }
}

// 싱글톤 인스턴스
const socketService = new SocketService();

// 전역 함수로 등록 (Android에서 호출)
window.updateAppState = (state) => {
  socketService.updateAppState(state);
};

// 브라우저의 visibility 변경 감지
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    socketService.updateAppState('background');
  } else {
    socketService.updateAppState('foreground');
  }
});

export default socketService;